import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../api/client';

// Ported from primus-admin-main's ScanAppsModal.jsx, dispatching through the
// cross-café internal endpoints. Difference: the "Add selected to catalog"
// step (POST /api/games) is itself café-scoped the same way /api/clientpc/
// and /api/chat/ were -- porting a cross-café games-catalog endpoint is out
// of scope for a PC-management deck, so this is review-only for now (the
// scan itself, the actual ask, works end-to-end).
const OVERLAY = { position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
const CARD = { background: '#111827', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 20, padding: '24px 26px', width: '100%', maxWidth: 640, maxHeight: '88vh', overflowY: 'auto', color: '#fff' };

export default function ScanAppsModal({ pc, cafeId, onClose }) {
    // phase: starting -> running -> review | error
    const [phase, setPhase] = useState('starting');
    const [items, setItems] = useState([]);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const commandIdRef = useRef(null);
    const [runToken, setRunToken] = useState(0);

    const start = useCallback(async () => {
        setPhase('starting');
        setItems([]);
        setError(null);
        commandIdRef.current = null;
        try {
            const r = await api.post(`/internal/pcs/${cafeId}/command`, { pc_id: pc.id, command: 'scan_installed_apps', params: null });
            commandIdRef.current = r.data?.id ?? null;
            setPhase('running');
        } catch (e) {
            setError(e.response?.data?.detail || 'Failed to start the scan.');
            setPhase('error');
        }
    }, [pc.id, cafeId]);

    useEffect(() => { start(); }, [start, runToken]);

    const ingest = useCallback((result) => {
        const games = Array.isArray(result?.games) ? result.games : [];
        const apps = Array.isArray(result?.apps) ? result.apps : [];
        const flat = [
            ...games.map((g) => ({ ...g, category: 'game' })),
            ...apps.map((a) => ({ ...a, category: 'app' })),
        ].filter((x) => x && x.name);
        setItems(flat);
        setPhase('review');
    }, []);

    useEffect(() => {
        if (phase !== 'running') return undefined;
        let stop = false;
        const startedAt = Date.now();
        const poll = async () => {
            try {
                const r = await api.get(`/internal/pcs/${cafeId}/history/${pc.id}`);
                const cmd = (r.data || []).find((c) => c.id === commandIdRef.current);
                if (cmd && !stop) {
                    if (cmd.state === 'SUCCEEDED') { ingest(cmd.result || {}); return; }
                    if (cmd.state === 'FAILED') { setError(cmd.result?.reason || 'The scan failed on the PC.'); setPhase('error'); return; }
                    if (cmd.state === 'EXPIRED') { setError('The kiosk did not respond (command expired).'); setPhase('error'); return; }
                }
            } catch { /* ignore, retry next tick */ }
            if (!stop && Date.now() - startedAt > 90000) {
                setError('Timed out waiting for the PC to report its apps.');
                setPhase('error');
            }
        };
        poll();
        const id = setInterval(poll, 2500);
        return () => { stop = true; clearInterval(id); };
    }, [phase, pc.id, cafeId, ingest]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((x) => (x.name || '').toLowerCase().includes(q));
    }, [items, search]);

    const games = filtered.filter((x) => x.category === 'game');
    const apps = filtered.filter((x) => x.category === 'app');

    const Row = (x) => (
        <div key={`${x.category}:${x.name}`} style={{ padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{x.name}</div>
            <div style={{ fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {x.launcher ? `${x.launcher} · ` : ''}{x.exe_path || '(no path)'}{x.launch_arguments ? `  ${x.launch_arguments}` : ''}
            </div>
        </div>
    );

    return (
        <div style={OVERLAY} onClick={onClose}>
            <div style={CARD} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700 }}>Scan apps — {pc.name}</h2>
                        <div style={{ fontSize: 13, color: '#9CA3AF', marginTop: 2 }}>Detect installed games/apps on this PC</div>
                    </div>
                    <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#9CA3AF', fontSize: 24, cursor: 'pointer', lineHeight: 1 }}>×</button>
                </div>

                {(phase === 'starting' || phase === 'running') && (
                    <div style={{ padding: '34px 0', textAlign: 'center', color: '#9CA3AF' }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>Scanning the PC…</div>
                        <div style={{ fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>
                            Reading installed Steam / Epic / Riot games and Windows apps.<br />Usually a few seconds (the PC must be online).
                        </div>
                    </div>
                )}

                {phase === 'error' && (
                    <div style={{ padding: '22px 0' }}>
                        <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 12, padding: 14, color: '#fca5a5', fontSize: 14 }}>
                            {error}
                        </div>
                    </div>
                )}

                {phase === 'review' && (
                    <div>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Filter by name…"
                            style={{ width: '100%', marginTop: 14, padding: '8px 12px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.18)', background: '#0b0f1a', color: '#fff', fontSize: 13 }}
                        />

                        {items.length === 0 && (
                            <div style={{ padding: '18px 0', color: '#9CA3AF', fontSize: 14 }}>No games or apps were detected on this PC.</div>
                        )}

                        {games.length > 0 && (
                            <>
                                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: '#e5e7eb', marginTop: 14 }}>Games ({games.length})</div>
                                <div style={{ marginTop: 4 }}>{games.map(Row)}</div>
                            </>
                        )}
                        {apps.length > 0 && (
                            <>
                                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: '#e5e7eb', marginTop: 18 }}>Apps ({apps.length})</div>
                                <div style={{ marginTop: 4 }}>{apps.map(Row)}</div>
                            </>
                        )}

                        {items.length > 0 && (
                            <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 14, lineHeight: 1.5 }}>
                                Review only from here — adding to a café's games catalog isn&apos;t available from the SuperAdmin panel yet.
                            </div>
                        )}
                    </div>
                )}

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 22, position: 'sticky', bottom: 0, background: '#111827', paddingTop: 12 }}>
                    {(phase === 'review' || phase === 'error') && (
                        <button type="button" onClick={() => setRunToken((t) => t + 1)} style={{ padding: '9px 20px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.2)', background: 'transparent', color: '#fff', cursor: 'pointer', fontSize: 14 }}>Re-scan</button>
                    )}
                    <button type="button" onClick={onClose} className="btn btn--primary">Close</button>
                </div>
            </div>
        </div>
    );
}
